import { tolkToneart } from '@/lib/sang/toneart';
import type { SangFelter } from './typer';

const DIREKTIV = /^\s*\{\s*([a-zA-Z_]+)\s*(?::\s*(.*?))?\s*\}\s*$/;
const ALIAS: Record<string, string> = { t: 'title', st: 'subtitle' };
const CHORDPRO_MARKOR = /\{\s*(?:(?:title|t|subtitle|st|artist|album|year|duration|key|tempo|time|capo|composer|lyricist)\s*:|soc\b|eoc\b|sov\b|start_of)/i;

const TONEART_LINJE = /^\s*(?:key|toneart|tonart)\s*:\s*(.*?)\s*$/i;
const META_LINJE = /^\s*(?:capo|tempo|bpm|takt|time|tid|tittel|title|artist)\s*:/i;

const rens = (v: string | undefined): string | null => {
  const t = (v ?? '').trim();
  return t === '' ? null : t;
};

function formaterLengde(v: string | null): string | null {
  if (!v) return null;
  if (/^\d+$/.test(v)) {
    const s = parseInt(v, 10);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }
  return v;
}

/** Tittel og artist fra filnavn: `Artist - Tittel.ext`, uten endelse og `(1)`. */
function fraFilnavn(filnavn: string): { tittel: string | null; artist: string | null } {
  const navn = filnavn
    .replace(/^.*[\\/]/, '')
    .replace(/\.[^.]+$/, '')
    .replace(/\s*\(\d+\)\s*$/, '')
    .trim();
  const del = navn.indexOf(' - ');
  if (del > 0) return { artist: rens(navn.slice(0, del)), tittel: rens(navn.slice(del + 3)) };
  return { artist: null, tittel: rens(navn) };
}

/**
 * `Key: G`, `Toneart: G` eller `Tonart: G` på egen linje før første akkord- eller tekstlinje (bare blanke linjer,
 * direktiver og metadata foran). Linjen fjernes. Utolkbar verdi gir ingen toneart, og linjen beholdes: den kan være sangtekst.
 */
function lesToneartLinje(tekst: string): { toneart: string | null; tekst: string } {
  const linjer = tekst.split('\n');
  for (let i = 0; i < linjer.length; i++) {
    const linje = linjer[i];
    const verdi = TONEART_LINJE.exec(linje)?.[1];
    if (verdi) {
      if (tolkToneart(verdi) !== null) return { toneart: verdi, tekst: linjer.filter((_, j) => j !== i).join('\n') };
      return { toneart: null, tekst };
    }
    if (linje.trim() !== '' && !DIREKTIV.test(linje) && !META_LINJE.test(linje)) break;
  }
  return { toneart: null, tekst };
}

export function tolkChordPro(raatekst: string, filnavn: string): SangFelter {
  const { toneart: fraLinje, tekst } = lesToneartLinje(raatekst);
  const m: Record<string, string> = {};
  for (const linje of tekst.split(/\r?\n/)) {
    const tr = DIREKTIV.exec(linje);
    if (!tr) continue;
    const navn = tr[1].toLowerCase();
    const noekkel = ALIAS[navn] ?? navn;
    const verdi = rens(tr[2]);
    if (verdi && m[noekkel] === undefined) m[noekkel] = verdi;
  }
  const reserve = fraFilnavn(filnavn);
  return {
    tittel: m.title ?? reserve.tittel,
    artist: m.artist ?? m.subtitle ?? reserve.artist,
    album: m.album ?? null,
    utgivelsesaar: m.year ?? null,
    lengde: formaterLengde(m.duration ?? null),
    toneart: m.key ?? fraLinje,
    bpm: m.tempo ?? null,
    takt: m.time ?? null,
    tekst_og_akkorder: tekst,
  };
}

export function tolkTekstfil(raatekst: string, filnavn: string): SangFelter {
  if (CHORDPRO_MARKOR.test(raatekst)) return tolkChordPro(raatekst, filnavn);
  const { toneart, tekst } = lesToneartLinje(raatekst);
  const { tittel, artist } = fraFilnavn(filnavn);
  return {
    tittel, artist, album: null, utgivelsesaar: null, lengde: null,
    toneart, bpm: null, takt: null, tekst_og_akkorder: tekst,
  };
}

/** Toneart fra siste ledd i filnavnet («Send-meg-E.pdf», «Sang [Bb].txt»). `---` skiller artist fra tittel og gir aldri toneart. */
export function tonartFraFilnavn(filnavn: string): string | null {
  const navn = filnavn
    .replace(/^.*[\\/]/, '')
    .replace(/\.[^.]+$/, '')
    .replace(/\s*\(\d+\)\s*$/, '')
    .trim();
  const m = /^(.*[^-_ ])([-_ ]+)[[(]?([A-G][#b]?m?)[\])]?$/.exec(navn);
  if (!m || /-{3,}/.test(m[2])) return null;
  return tolkToneart(m[3]) !== null ? m[3] : null;
}
